import { useMemo, useState } from 'react';
import { Button } from '@monksflow/monks-ui';
import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import type { SchemaWidget } from '@/shared/ui/SchemaForm';
import { ToolConfigForm } from './ToolConfigForm';

interface ScopeTabProps {
  toolId: string;
  jsonSchema: Record<string, unknown>;
  existingConfig: { data: Record<string, unknown> } | null;
  description: string;
  onSave: (data: Record<string, unknown>) => void | Promise<void>;
  onDelete: () => void | Promise<void>;
  isSaving: boolean;
  isRemoving: boolean;
  uiSchemaOverride?: Record<string, unknown>;
  /** Control for `kbSelect`. Injected by the page (dependency injection). */
  kbSelectWidget?: SchemaWidget;
  /** Control for `kbMultiSelect`. Injected by the page (dependency injection). */
  kbMultiSelectWidget?: SchemaWidget;
}

/**
 * The editor state is seeded from the saved configuration, so a different saved
 * configuration has to start a fresh editor. Remounting on a change of the saved
 * content does that without an effect — and unlike an effect keyed on object
 * identity, a background refetch that returns the same data no longer discards
 * whatever the person was in the middle of typing.
 */
export function ScopeTab(props: ScopeTabProps) {
  return <ScopeTabEditor key={JSON.stringify(props.existingConfig?.data ?? null)} {...props} />;
}

function ScopeTabEditor({
  toolId,
  jsonSchema,
  existingConfig,
  description,
  onSave,
  onDelete,
  isSaving,
  isRemoving,
  uiSchemaOverride,
  kbSelectWidget,
  kbMultiSelectWidget,
}: ScopeTabProps) {
  const { t } = useTranslation('admin');

  const [showForm, setShowForm] = useState(!!existingConfig);
  const [formData, setFormData] = useState<Record<string, unknown>>(existingConfig?.data ?? {});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const originalData = useMemo(
    () => JSON.stringify(existingConfig?.data ?? {}),
    [existingConfig?.data],
  );
  const isDirty = JSON.stringify(formData) !== originalData;

  const handleSave = async () => {
    await onSave(formData);
  };

  const handleDelete = async () => {
    await onDelete();
    setShowDeleteConfirm(false);
    setShowForm(false);
    setFormData({});
  };

  if (!showForm) {
    return (
      <div className="flex flex-col gap-6">
        <p className="text-sm text-muted-foreground">{description}</p>
        <div className="flex flex-col items-center justify-center gap-2 p-16 text-center">
          <p className="text-base text-muted-foreground">
            {t('admin.scopeTab.noConfigurationSetForThis')}
          </p>
          <p className="mb-4 text-sm text-muted-foreground">{description}</p>
          <Button type="button" onClick={() => setShowForm(true)}>
            <PlusIcon aria-hidden="true" className="size-4" />
            {t('admin.scopeTab.createConfiguration')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-muted-foreground">{description}</p>

      <ToolConfigForm
        toolId={toolId}
        jsonSchema={jsonSchema}
        formData={formData}
        onChange={setFormData}
        uiSchemaOverride={uiSchemaOverride}
        kbSelectWidget={kbSelectWidget}
        kbMultiSelectWidget={kbMultiSelectWidget}
      />

      <div className="flex flex-row justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          className="text-destructive"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isRemoving || !existingConfig}
        >
          {t('admin.scopeTab.delete')}
        </Button>
        <Button type="button" onClick={() => void handleSave()} disabled={isSaving || !isDirty}>
          {isSaving ? t('admin.scopeTab.saving') : t('admin.scopeTab.save')}
        </Button>
      </div>

      <DeleteConfirmationModal
        open={showDeleteConfirm}
        isDeleting={isRemoving}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirmDelete={() => void handleDelete()}
        customModalTitle={t('admin.scopeTab.deleteConfigurationTitle')}
        warningMessage={t('admin.scopeTab.thisWillRemoveTheConfiguration')}
      />
    </div>
  );
}
